import moment from 'moment';
import EventCard from '../EventCard';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Typography, Container, Button, Skeleton } from '@mui/material';
import { selectAllEvents, selectEventsStatus } from '../../redux/eventsSlice';

const UpcomingEvents = () => {
  const navigate = useNavigate();
  const events = useSelector(selectAllEvents);
  const status = useSelector(selectEventsStatus);
  const upcomingList = Array.isArray(events) ? events.filter(e => moment(e.date).isAfter(moment())) : [];

  const isLoading = status === 'loading' || status === 'idle';
  const hasEvents = upcomingList.length > 0;

  if (!isLoading && !hasEvents) return null;

  return (
    <Box
      sx={{
        bgcolor: '#0a0a0a',
        py: { xs: 10, md: 15 },
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(circle at 20% 30%, #111827 0%, #0a0a0a 100%)'
      }}
    >
      {/* Background Decoration Text */}
      <Typography
        variant="h1"
        sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: { xs: '8rem', md: '15rem' },
          fontWeight: 900,
          color: 'transparent',
          WebkitTextStroke: '1px rgba(255,255,255,0.05)',
          userSelect: 'none',
          zIndex: 0,
          whiteSpace: 'nowrap',
          fontFamily: 'Bebas Neue'
        }}
      >
        EVENTS
      </Typography>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ mb: { xs: 8, md: 12 }, textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{
              color: '#fbbf24',
              fontWeight: 700,
              letterSpacing: '3px',
              mb: 1,
              display: 'block'
            }}
          >
            Don't Miss Out
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Bebas Neue',
              mb: 3,
              color: '#ffffff',
              fontSize: { xs: '3.5rem', md: '5rem' },
              letterSpacing: '2px',
              textShadow: '0 10px 30px rgba(0,0,0,0.5)'
            }}
          >
            Upcoming <Box component="span" sx={{ color: '#fbbf24' }}>Championships</Box>
          </Typography>
          <Box
            sx={{
              width: '80px',
              height: '4px',
              bgcolor: '#fbbf24',
              margin: '0 auto 24px',
              borderRadius: '2px'
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              color: '#94a3b8',
              maxWidth: '650px',
              margin: '0 auto',
              fontSize: '1.25rem',
              lineHeight: 1.6
            }}
          >
            Experience the thrill of elite Gymkhana competitions. Track the next roar of engines and witness history in the making.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-around" }}>
          {isLoading ? (
            // Skeleton Loader
            [...Array(6)].map((_, index) => (
              <Box
                key={`skeleton-${index}`}
                sx={{
                  width: { xs: '100%', sm: "48%", md: "31%", xxl: "23%" },
                  aspectRatio: '4 / 5',
                  borderRadius: '25px',
                  mb: 3,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&::after': {
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)'
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    p: 3,
                    zIndex: 1
                  }}
                >
                  <Skeleton width="40%" height={20} sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Skeleton width="80%" height={40} sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.2)' }} />
                  <Skeleton width="90%" height={60} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
                </Box>
              </Box>
            ))
          ) : (
            upcomingList.slice(0, 6).map((item, index) => (
              <EventCard
                key={item.id || index}
                index={index}
                date={item.date}
                title={item.title}
                imageUrl={item.imageUrl}
                description={item.description}
                onAction={() => navigate('/events')}
              />
            ))
          )}
        </Box>

        {/* Bottom Call to Action */}
        <Box sx={{ mt: 10, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/events')}
            endIcon={<ArrowForwardIcon />}
            sx={{
              color: '#ffffff',
              borderColor: 'rgba(255,255,255,0.3)',
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              borderRadius: '0',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#fbbf24',
                color: '#fbbf24',
                bgcolor: 'rgba(251, 191, 36, 0.05)',
                transform: 'translateY(-3px)'
              }
            }}
          >
            Explore all events
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default UpcomingEvents;